import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { LoomContext } from 'containers/LoomProvider'
import { ReactTooltipStyled } from 'components/styled/typography/typographyExtentions'
import { insertLoom, setLoomStatus } from 'actions/loom'
import { showModal } from 'actions/_modal'
import { RecordButtonStyled } from './styles'
import useUnsavedWorkWarning from '../../../hooks/useUnsavedWorkWarning'

// Link to loomSDK docs
// https://www.notion.so/loom/loomSDK-public-beta-docs-97a52ce6065549859fb1619b4c7f16d4
const RecordButton = ({
  sessionId, // must uniquely identify the button (and associated editor)
  recoveryPath, // path to navigate to for insertion
  recoveryAction, // function that will be called on insertion (intened for recovering a modal)
  externalTooltipId, // optionally use an external tooltip with the specified id (instead of making a new one)
}) => {
  const { supported, notSupportedReasons, loom } = useContext(LoomContext)

  const ref = useRef(null)

  const globalLoomSession = useSelector(state => state.loom)
  const dispatch = useDispatch()

  const active = globalLoomSession.active
  const disabled = !supported || active || !loom

  useUnsavedWorkWarning(
    active,
    `loom-recording-${sessionId}`,
    '/' // recording is persisted throughout the app, but not on page close
  )

  const setStatus = status => {
    dispatch(setLoomStatus(
      sessionId,
      status === 'active-recording' || status === 'post-recording'
    ))
  }

  const insertHandler = loomData => {
    if (loomData) {
      dispatch(insertLoom(sessionId, loomData))
    } else {
      dispatch(showModal('GENERAL_ERROR'))
    }
    if (recoveryPath && recoveryPath !== window.location.pathname) {
      dispatch(push(recoveryPath))
    }
    if (recoveryAction) {
      recoveryAction()
    }
  }

  useLayoutEffect(() => {
    if (!disabled && ref.current && loom) {
      loom.configureButton({
        element: ref.current,
        hooks: {
          onInsertClicked: insertHandler,
          onLifecycleUpdate: setStatus
        }
      })
    }
  }, [loom, disabled, sessionId, recoveryPath, recoveryAction])

  const tooltipText = 
    supported
      ? active
        ? 'Recording in progress.'
        : 'Insert a video recording.'
      : notSupportedReasons === 'third-party-cookies-disabled'
        ? 'Video recording not available in incognito mode.'
        : notSupportedReasons === 'incompatible-browser' ||
          notSupportedReasons === 'no-media-streams-support'
          ? 'Video recording not yet supported by this browser.'
          : 'An error occured.'

  return (
    <div style={{ display: 'flex' }}>
      <div
        data-tip={tooltipText}
        data-for={externalTooltipId || `record-button-${sessionId}`}
      >
        <RecordButtonStyled disabled={disabled} ref={ref} />
      </div>
      {!externalTooltipId && (
        <ReactTooltipStyled
          id={`record-button-${sessionId}`}
          className="tooltip"
          effect="solid"
          multiline={true}
          place="right"
        />
      )}
    </div>
  )
}

RecordButton.propTypes = {
  sessionId: PropTypes.string.isRequired,
  recoveryPath: PropTypes.string,
  recoveryAction: PropTypes.func,
  externalTooltipId: PropTypes.string
}

export default RecordButton
